import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  flowDefinitionService,
  FlowDefinition,
  FlowNodeType,
  CreateFlowDefinitionRequest,
  UpdateFlowDefinitionRequest,
} from '../../services/flowDefinitionService';

interface FlowDefinitionState {
  definitions: FlowDefinition[];
  nodeTypes: FlowNodeType[];
  currentId: string | null;
  loading: boolean;
  error: string | null;
  dirty: boolean;
}

const initialState: FlowDefinitionState = {
  definitions: [],
  nodeTypes: [],
  currentId: null,
  loading: false,
  error: null,
  dirty: false,
};

export const fetchFlowDefinitions = createAsyncThunk(
  'flowDefinition/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await flowDefinitionService.getAll();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch flow definitions');
    }
  },
);

export const fetchNodeTypes = createAsyncThunk(
  'flowDefinition/fetchNodeTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await flowDefinitionService.getNodeTypes();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch node types');
    }
  },
);

export const createFlowDefinition = createAsyncThunk(
  'flowDefinition/create',
  async (data: CreateFlowDefinitionRequest, { rejectWithValue }) => {
    try {
      return await flowDefinitionService.create(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create flow definition');
    }
  },
);

export const updateFlowDefinition = createAsyncThunk(
  'flowDefinition/update',
  async ({ id, data }: { id: string; data: UpdateFlowDefinitionRequest }, { rejectWithValue }) => {
    try {
      return await flowDefinitionService.update(id, data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update flow definition');
    }
  },
);

export const deleteFlowDefinition = createAsyncThunk(
  'flowDefinition/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await flowDefinitionService.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete flow definition');
    }
  },
);

const flowDefinitionSlice = createSlice({
  name: 'flowDefinition',
  initialState,
  reducers: {
    setCurrentId(state, action: PayloadAction<string | null>) {
      state.currentId = action.payload;
    },
    setDirty(state, action: PayloadAction<boolean>) {
      state.dirty = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch definitions
      .addCase(fetchFlowDefinitions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFlowDefinitions.fulfilled, (state, action) => {
        state.loading = false;
        state.definitions = action.payload;
      })
      .addCase(fetchFlowDefinitions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch node types
      .addCase(fetchNodeTypes.fulfilled, (state, action) => {
        state.nodeTypes = action.payload;
      })
      // Create
      .addCase(createFlowDefinition.fulfilled, (state, action) => {
        state.definitions.unshift(action.payload);
        state.currentId = action.payload.id;
        state.dirty = false;
      })
      // Update
      .addCase(updateFlowDefinition.fulfilled, (state, action) => {
        const index = state.definitions.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.definitions[index] = action.payload;
        }
        state.dirty = false;
      })
      // Delete
      .addCase(deleteFlowDefinition.fulfilled, (state, action) => {
        state.definitions = state.definitions.filter((d) => d.id !== action.payload);
        if (state.currentId === action.payload) {
          state.currentId = state.definitions[0]?.id ?? null;
        }
      });
  },
});

export const { setCurrentId, setDirty } = flowDefinitionSlice.actions;
export default flowDefinitionSlice.reducer;
